import type { ReactElement, ReactNode } from 'react';

import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

export interface ProgressBarProps {
  /**
   * Progress bar label
   */
  progressBarLabel?: string | ReactElement;
  /**
   * Progress completion - Out of completion value
   */
  outOfCompletionValue?: ReactNode;
  /**
   * Progress completion - Out of completion value unit
   */
  outOfCompletionValueUnit?: string;
  /**
   * Progress completion - Out of completion value
   */
  outOfCompletionTotalValue?: ReactNode;
  /**
   * Progress completion - Out of completion value unit
   */
  outOfCompletionTotalValueUnit?: string;
  /**
   * Progress completion - accepted values from 0 to 100
   */
  completionPercentValue?: number;
  /**
   * Progress bar background color
   */
  barThickness?: 'thin' | 'thick';
  /**
   * Progress bar background color
   */
  barBackgroundColor?:
    | 'blue'
    | 'orange'
    | 'green'
    | 'gradientBackground'
    | 'auto'
    | 'gray'
    | 'red';
  /**
   * If percents should be hidden
   */
  hidePercentValue?: boolean;
  /**
   * If progress bar should display as compact variant
   */
  isCompact?: boolean;
  /**
   * Progress bar label additional CSS classes
   */
  additionalLabelClasses?: string;
  /**
   * Progress bar completion values container additional CSS classes
   */
  additionalCompletionValuesContainerClasses?: string;
  /**
   * Progress bar percent completion values container additional CSS classes
   */
  additionalCompletionPercentValueClasses?: string;
  /**
   * Progress bar additional CSS classes
   */
  additionalBarClasses?: string;
  /**
   * Progress bar container additional CSS classes
   */
  additionalContainerClasses?: string;
  /**
   * Progress bar total value additional CSS classes
   */
  additionalCompletionTotalValuesClasses?: string;
  /**
   * Component test id
   */
  testId?: string;
}

export const ProgressBar = ({
  progressBarLabel = '',
  outOfCompletionValue = null,
  outOfCompletionValueUnit = '',
  outOfCompletionTotalValue = '',
  outOfCompletionTotalValueUnit = '',
  completionPercentValue = 0,
  barThickness = 'thin',
  barBackgroundColor = 'auto',
  hidePercentValue = false,
  additionalLabelClasses = '',
  additionalCompletionValuesContainerClasses = '',
  additionalCompletionTotalValuesClasses = '',
  additionalCompletionPercentValueClasses = '',
  additionalBarClasses = '',
  additionalContainerClasses = '',
  testId = '',
}: ProgressBarProps) => {
  const thicknessClass = {
    thin: 'h-2',
    thick: 'h-3',
  };
  const backgroundColorClass = {
    blue: 'bg-blue',
    yellow: 'bg-orange-400 dark:bg-orange-500',
    orange: 'bg-orange-400 dark:bg-orange-600',
    green: 'bg-lime',
    red: 'bg-red-600 dark:bg-red-700',
    gray: 'bg-slate-100 dark:bg-slate-700',
    gradientBackground: 'bg-blue',
  };

  const barColor = useMemo(() => {
    if (barBackgroundColor !== 'auto') return barBackgroundColor;
    if (
      (!outOfCompletionTotalValue && isFinite(completionPercentValue)) ||
      isNaN(completionPercentValue)
    )
      return 'gray';
    if (completionPercentValue < 70) return 'blue';
    if (completionPercentValue < 90) return 'yellow';
    if (completionPercentValue < 100) return 'orange';
    return 'red';
  }, [barBackgroundColor, completionPercentValue, outOfCompletionTotalValue]);

  return (
    <div
      className={twMerge(
        'flex flex-col items-start text-black w-full',
        additionalContainerClasses,
      )}
      {...getTestProps(testId, 'wrapper')}
    >
      {progressBarLabel && (
        <p
          className={twMerge(
            'text-slate-400 text-sm/none font-light mb-2',
            additionalLabelClasses,
          )}
        >
          {progressBarLabel}
        </p>
      )}

      <div className="flex items-end">
        {outOfCompletionValue != null && (
          <div
            className={twMerge(
              'font-medium mb-1 whitespace-nowrap leading-none',
              additionalCompletionValuesContainerClasses,
            )}
          >
            <span className="font-bold text-sm/none dark:text-slate-200">
              {outOfCompletionValue}
            </span>
            <span className="font-bold text-sm/none dark:text-slate-200">
              {' '}
              {outOfCompletionValueUnit}
            </span>
            <span className="dark:text-slate-200">
              {' '}
              {outOfCompletionTotalValue != null ? '/ ' : ''}
            </span>
            <span
              className={twMerge(
                'font-normal text-sm dark:text-slate-200',
                additionalCompletionTotalValuesClasses,
              )}
            >
              {outOfCompletionTotalValue}
            </span>
            <span
              className={twMerge(
                'font-normal dark:text-slate-200',
                additionalCompletionTotalValuesClasses,
              )}
            >
              {' '}
              {outOfCompletionTotalValueUnit}
            </span>
          </div>
        )}
      </div>

      <div
        className={twMerge('flex items-center w-full', additionalBarClasses)}
        {...getTestProps(testId, 'indicator-wrap')}
      >
        <div
          className={twMerge(
            'w-full bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden',
            thicknessClass[barThickness],
          )}
        >
          <div
            className={twMerge(
              'rounded-full dark:text-slate-200',
              thicknessClass[barThickness],
              backgroundColorClass[barColor],
            )}
            style={{
              width: `${
                !isFinite(completionPercentValue) ? 100 : completionPercentValue
              }%`,
            }}
            {...getTestProps(testId, 'indicator')}
          />
        </div>

        {!hidePercentValue ? (
          <p
            className={twMerge(
              'whitespace-nowrap min-w-[50px] w-[50px]! ml-3 text-lg/none font-semibold dark:text-slate-200',
              additionalCompletionPercentValueClasses,
            )}
          >
            {isNaN(completionPercentValue) ? (
              'N/A'
            ) : (
              <>
                <span>{completionPercentValue.toFixed(0)}</span>%
              </>
            )}
          </p>
        ) : (
          <p
            className={twMerge(
              'whitespace-nowrap min-w-[50px] w-[50px]! ml-3 text-lg/none font-semibold font-ibm dark:text-slate-200',
              additionalCompletionPercentValueClasses,
            )}
          />
        )}
      </div>
    </div>
  );
};

export default ProgressBar;
